/**
 * <pdm-new-arrivals-tabs>
 * Fetches the newest products for each tab in one Storefront API request (plus up
 * to 3 extra Timeless pages) and renders an independent Swiper slider per tab.
 *
 * Only in-stock products are requested (available_for_sale / available filter).
 *
 * Only watches: the Timeless collection also holds jewelry and the API ignores a
 * productType filter there (not enabled in Search & Discovery), so its products
 * are filtered client-side and extra pages are requested until N watches.
 *
 * Duplicates: Timeless (collection) wins. Its products are removed from the
 * Watches / Pre-owned lists, which are over-fetched to keep N items per tab.
 */
if (!customElements.get("pdm-new-arrivals-tabs")) {
  const API_URL =
    "https://cj-charles-jewelers.myshopify.com/api/2026-01/graphql.json";

  const PRODUCT_FIELDS = `
    id
    productType
    vendor
    handle
    title
    availableForSale
    featuredImage { url altText }
    images(first: 2) { nodes { url altText } }
    priceRange {
      minVariantPrice { amount }
      maxVariantPrice { amount }
    }
  `;

  const WATCH_TYPES = ["Watches", "Preowned Watches"];
  const isWatch = (product) => WATCH_TYPES.includes(product.productType);

  // Safety cap for the extra Timeless requests when a page is mostly jewelry
  const MAX_TIMELESS_PAGES = 3;

  // Extra items cover the ones removed (already in Timeless, or jewelry in Timeless)
  const getFetchCount = (items) => Math.min(items * 2, 250);

  const buildTimelessQuery = (timelessHandle, count, after = null) => `
    timeless: collection(handle: ${JSON.stringify(timelessHandle)}) {
      products(first: ${count}, sortKey: CREATED, reverse: true, filters: { available: true }${after ? `, after: ${JSON.stringify(after)}` : ""}) {
        nodes { ${PRODUCT_FIELDS} }
        pageInfo { hasNextPage endCursor }
      }
    }`;

  const buildQuery = (items, timelessHandle) => {
    const fetchCount = getFetchCount(items);

    return `{
      watches: products(first: ${fetchCount}, sortKey: CREATED_AT, reverse: true, query: "product_type:\\"Watches\\" AND NOT product_type:\\"Preowned Watches\\" AND available_for_sale:true") {
        nodes { ${PRODUCT_FIELDS} }
      }
      preowned: products(first: ${fetchCount}, sortKey: CREATED_AT, reverse: true, query: "product_type:\\"Preowned Watches\\" AND available_for_sale:true") {
        nodes { ${PRODUCT_FIELDS} }
      }
      ${buildTimelessQuery(timelessHandle, fetchCount)}
    }`;
  };

  const formatPrice = (price) =>
    Number(price)
      .toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
      })
      .replace("$", "$ ");

  // Defer scripts in the section run before Swiper (loaded at the end of theme.liquid)
  const swiperReady = () =>
    new Promise((resolve) => {
      if (typeof Swiper !== "undefined" || document.readyState !== "loading") {
        resolve();
        return;
      }
      document.addEventListener("DOMContentLoaded", resolve, { once: true });
    });

  class PdmNewArrivalsTabs extends HTMLElement {
    connectedCallback() {
      this.items = Number(this.dataset.items) || 12;
      this.timelessHandle = this.dataset.timelessHandle || "one-of-a-kind";
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.swipers = {};

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", () => this.selectTab(tab));
        tab.addEventListener("keydown", (event) => this.onTabKeydown(event));
      });

      // Only build for exposed users (flag may resolve before or after this element connects)
      this.onFlagEnabled = () => {
        if (this.loaded) return;
        this.loaded = true;
        this.load();
      };

      if (document.body.dataset.newArrivalsTabs === "true") {
        this.onFlagEnabled();
      } else {
        document.addEventListener("new-arrivals-tabs:enabled", this.onFlagEnabled);
      }
    }

    disconnectedCallback() {
      document.removeEventListener("new-arrivals-tabs:enabled", this.onFlagEnabled);
      Object.values(this.swipers).forEach((swiper) => swiper.destroy());
      this.swipers = {};
    }

    async load() {
      const lists = await this.fetchProducts();
      if (!lists) {
        this.hidden = true;
        return;
      }

      for (const [tabId, products] of Object.entries(lists)) {
        const panel = this.getPanel(tabId);
        if (!panel) continue;

        if (products.length === 0) {
          this.removeTab(tabId);
          continue;
        }
        this.renderProducts(panel, products);
      }

      const activeTab =
        this.tabs.find((tab) => tab.getAttribute("aria-selected") === "true") ||
        this.tabs[0];

      if (!activeTab) {
        this.hidden = true;
        return;
      }

      await swiperReady();
      this.selectTab(activeTab, false);
      this.classList.add("shown");
    }

    async request(query) {
      const response = await fetch(API_URL, {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });
      const { data } = await response.json();
      return data || null;
    }

    // Keep paging the Timeless collection (skipping jewelry) until there are enough watches
    async collectTimelessWatches(connection) {
      const watches = [];
      let page = connection;
      let extraPages = 0;

      while (page) {
        watches.push(...page.nodes.filter(isWatch));

        const isComplete = watches.length >= this.items;
        if (isComplete || !page.pageInfo?.hasNextPage || extraPages >= MAX_TIMELESS_PAGES) break;

        extraPages += 1;
        try {
          const data = await this.request(
            `{ ${buildTimelessQuery(this.timelessHandle, getFetchCount(this.items), page.pageInfo.endCursor)} }`,
          );
          page = data?.timeless?.products || null;
        } catch (error) {
          // Keep the watches already collected instead of hiding the whole section
          console.error("pdm-new-arrivals-tabs:", error);
          break;
        }
      }

      return watches.slice(0, this.items);
    }

    async fetchProducts() {
      try {
        const data = await this.request(buildQuery(this.items, this.timelessHandle));
        if (!data) return null;

        const timeless = await this.collectTimelessWatches(data.timeless?.products);
        const timelessIds = new Set(timeless.map((product) => product.id));
        const pick = (nodes, productType) =>
          (nodes || [])
            .filter(
              (product) =>
                product.productType === productType &&
                !timelessIds.has(product.id),
            )
            .slice(0, this.items);

        return {
          watches: pick(data.watches?.nodes, "Watches"),
          timeless,
          preowned: pick(data.preowned?.nodes, "Preowned Watches"),
        };
      } catch (error) {
        console.error("pdm-new-arrivals-tabs:", error);
        return null;
      }
    }

    renderProducts(panel, products) {
      const template = this.querySelector(".pdm-new-arrivals-tabs__template");
      const wrapper = panel.querySelector(".swiper-wrapper");
      if (!template || !wrapper) return;

      const fragment = document.createDocumentFragment();

      for (const product of products) {
        const node = template.content.cloneNode(true);

        const link = node.querySelector(".pdm-new-arrivals-tabs__item");
        link.href = `${window.Shopify.routes.root}products/${product.handle}`;
        link.setAttribute("title", product.title);

        const img = node.querySelector(".item__image-main");
        if (product.featuredImage) {
          img.src = product.featuredImage.url;
          img.alt = product.featuredImage.altText || product.title;
        } else {
          img.remove();
        }

        const img2 = node.querySelector(".item__image-hover");
        const hoverImage = product.images.nodes[1];
        if (hoverImage) {
          img2.src = hoverImage.url;
          img2.alt = hoverImage.altText || product.title;
        } else {
          img2.remove();
        }

        node.querySelector(".item__title").textContent = product.title;
        node.querySelector(".item__vendor").textContent = product.vendor;

        const price = node.querySelector(".item__price");
        const { minVariantPrice, maxVariantPrice } = product.priceRange;
        if (!product.availableForSale) {
          price.textContent = "Sold Out";
        } else if (minVariantPrice.amount !== maxVariantPrice.amount) {
          price.textContent = `${formatPrice(minVariantPrice.amount)} - ${formatPrice(maxVariantPrice.amount)}`;
        } else {
          price.textContent = formatPrice(minVariantPrice.amount);
        }

        fragment.appendChild(node);
      }

      wrapper.appendChild(fragment);
    }

    selectTab(tab, focus = true) {
      this.tabs.forEach((item) => {
        const isActive = item === tab;
        item.setAttribute("aria-selected", String(isActive));
        item.tabIndex = isActive ? 0 : -1;

        const panel = this.getPanel(item.dataset.tab);
        if (panel) panel.hidden = !isActive;
      });

      if (focus) tab.focus();
      this.initSwiper(tab.dataset.tab);
    }

    // Swipers are created when their panel is first visible so Swiper can measure it
    initSwiper(tabId) {
      if (this.swipers[tabId]) {
        this.swipers[tabId].update();
        return;
      }

      const panel = this.getPanel(tabId);
      const container = panel?.querySelector(".swiper");
      if (!container || typeof Swiper === "undefined") return;

      this.swipers[tabId] = new Swiper(container, {
        slidesPerView: 2,
        slidesPerGroup: 2,
        spaceBetween: 12,
        autoplay: false,
        loop: false,
        speed: 400,
        navigation: {
          nextEl: panel.querySelector(".swiper-button-next"),
          prevEl: panel.querySelector(".swiper-button-prev"),
        },
        breakpoints: {
          768: {
            slidesPerView: 4,
            slidesPerGroup: 4,
            spaceBetween: 32,
          },
        },
      });
    }

    onTabKeydown(event) {
      const index = this.tabs.indexOf(event.currentTarget);
      const keys = {
        ArrowRight: index + 1,
        ArrowLeft: index - 1,
        Home: 0,
        End: this.tabs.length - 1,
      };
      if (!(event.key in keys)) return;

      event.preventDefault();
      const nextIndex = (keys[event.key] + this.tabs.length) % this.tabs.length;
      this.selectTab(this.tabs[nextIndex]);
    }

    removeTab(tabId) {
      const tab = this.tabs.find((item) => item.dataset.tab === tabId);
      if (tab) tab.remove();
      this.tabs = this.tabs.filter((item) => item !== tab);
      this.getPanel(tabId)?.remove();
    }

    getPanel(tabId) {
      return this.querySelector(`[data-panel="${tabId}"]`);
    }
  }

  customElements.define("pdm-new-arrivals-tabs", PdmNewArrivalsTabs);
}
